/**
 * Клас ініціалізації методів для хедера.
 * 
 * @package AGDS
 * @category Modules
 * @module Header
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class Header {

	/**
	 * Конструктор
	 *
	 * @param {Object} options
	 */
	constructor(options = {}) {
		this.windowWidth = options.windowWidth
		this.slideUp = options.animations.slideUp
		this.slideDown = options.animations.slideDown
		this.overlay = options.overlay
		this.header = options.header
	}

	/**
	 * Ініціалізація
	 * 
	 * @returns {void}
	 */
	init() {
		if (!this.header) {
			console.warn('Header element not found')
			return
		}

		// Ініціалізація змінних.
		this.body = document.body
		this.prevScroll = window.scrollY || document.documentElement.scrollTop

		// Ініціалізація
		this.headerScrollInit()

		if (this.windowWidth < 1200) {
			this.burgerNav()
			this.navSubMenu()
		}
	}

	/**
	 * Ініціалізує поведінку хедера при скролі сторінки.
	 */
	headerScrollInit() {
		this.toggleHeaderTransparency(document.scrollingElement.scrollTop)
		window.addEventListener('scroll', () => this.onScroll())
	}

	/**
	 * Обробник події скролу сторінки.
	 * 
	 * @private
	 */
	onScroll() {
		const curScroll = window.scrollY || document.documentElement.scrollTop
		const direction = curScroll > this.prevScroll ? 2 : curScroll < this.prevScroll ? 1 : 0

		this.toggleHeaderVisibility(curScroll, direction)
		this.toggleHeaderTransparency(curScroll)
		this.prevScroll = curScroll
	}

	/**
	 * Керує видимістю хедера залежно від напрямку скролу.
	 * 
	 * @param {number} curScroll - Поточна позиція скролу
	 * @param {number} direction - Напрямок скролу (1 - вгору, 2 - вниз, 0 - без змін)
	 * @private
	 */
	toggleHeaderVisibility(curScroll, direction) {
		const classHide = 'header--hide'
		if (direction === 2 && curScroll > 1) {
			this.header.classList.add(classHide)
			this.body.classList.add(classHide)
		} else if (direction === 1) {
			this.header.classList.remove(classHide)
			this.body.classList.remove(classHide)
		}
	}

	/**
	 * Керує прозорістю хедера залежно від позиції скролу.
	 *
	 * @param {number} scrollTop - Позиція скролу від верху сторінки
	 * @private
	 */
	toggleHeaderTransparency(scrollTop) {
		const classTranp = 'header--transparent'
		if (this.body.classList.contains('header-transparent') && scrollTop === 0) {
			this.header.classList.add(classTranp)
			this.body.classList.add(classTranp)
		} else {
			this.header.classList.remove(classTranp)
			this.body.classList.remove(classTranp)
		}
	}

	/**
	 * Ініціалізує burger-меню.
	 *
	 * @returns {void}
	 */
	burgerNav() {
		const header = this.header
		const overlay = this.overlay
		const burger = header.querySelector('.burger')
		const headerMenu = header.querySelector('.header--nav')
		const links = headerMenu?.querySelectorAll('.menu-item a')

		if (!burger || !headerMenu) {
			return
		}

		const openNav = () => {
			burger.classList.toggle('_active')
			header.classList.toggle('header-nav--active')
			headerMenu.classList.toggle('_show')
			document.documentElement.classList.toggle('_lock')
			overlay.classList.toggle('_show')
		}

		const closeNav = () => {
			burger.classList.remove('_active')
			header.classList.remove('header-nav--active')
			headerMenu.classList.remove('_show')
			document.documentElement.classList.remove('_lock')
			overlay.classList.remove('_show')
		}

		burger.onclick = openNav
		overlay.onclick = closeNav

		if (links && links.length) {
			links.forEach(link => {
				const href = link.getAttribute('href')?.trim()
				if (!href) return

				const isTriggerOnly = href === '#' || href.startsWith('javascript:')
				if (!isTriggerOnly) {
					link.onclick = closeNav
				}
			})
		}
	}

	/**
	 * Ініціалізує роботу субменю для елементів, що мають дочірні меню.
	 *
	 * @returns {void}
	 */
	navSubMenu() {
		const header = this.header

		//Одразу ховаємо всі субменю
		const allSubMenus = header.querySelectorAll('.menu-item-has-children .sub-menu')
		allSubMenus.forEach(menu => {
			menu.setAttribute('hidden', '')
			menu.classList.remove('_active')
			if (menu.parentElement) menu.parentElement.classList.remove('_active')
		})

		// Видаляємо старий слухач, щоб уникнути дублювання подій
		if (this._navSubMenuHandler) {
			header.removeEventListener('click', this._navSubMenuHandler)
		}

		// Основна функція-обробник кліків
		const handleMenuClick = (e) => {
			const icon = e.target.closest('.menu-item--icon')
			const link = e.target.closest('a')

			let trigger = null

			if (icon) {
				trigger = icon
			} else if (link) {
				const href = link.getAttribute('href')
				if (!href || href === '#' || href.startsWith('javascript')) {
					trigger = link
				}
			}

			// Якщо клік не по наших елементах — виходимо
			if (!trigger) return

			// Знаходимо батьківський пункт меню (li)
			const parentItem = trigger.closest('.menu-item-has-children')
			if (!parentItem) return

			// Знаходимо субменю всередині цього пункту
			const targetMenu = parentItem.querySelector('.sub-menu')
			if (!targetMenu) return

			// Зупиняємо стандартну поведінку (перехід за посиланням)
			e.preventDefault()
			e.stopPropagation()

			// Логіка перемикання (Toggle)
			const isActive = targetMenu.classList.contains('_active')

			if (isActive) {
				this.slideUp(targetMenu)
				targetMenu.classList.remove('_active')
				parentItem.classList.remove('_active')

			} else {
				this.slideDown(targetMenu)
				targetMenu.classList.add('_active')
				parentItem.classList.add('_active')
			}
		};

		// Зберігаємо посилання на функцію та вішаємо слухач
		this._navSubMenuHandler = handleMenuClick
		header.addEventListener('click', handleMenuClick)
	}

}
